'use client'

import { prepareForSlot } from '@mui/base'
import { type FC } from 'react'
import { FormattedMessage } from 'react-intl'

import { type CachedHierarchyEntryResponseDto } from '@redteclab/api/clients/bully'
import { IconArrowRight, IconCaretLeft } from '@redteclab/icons'
import {
  Breadcrumb,
  Breadcrumbs,
  Link as WebUiLink,
} from '@redteclab/web-ui-react'

import { Button, Heading, Link } from 'base-ui'

import { NextLink } from '../../../../next-link'
import { useNextPathname } from '../../../../next-pathname'
import { storyblokGetHierarchyCategoryList } from '../../../../storyblok'
import { urlNormalizeUri } from '../../../../url-handling'
import { cmsStoryblokSlugToPublicUrl } from '../../../cmsStoryblokSlugToPublicUrl'
import { useStoryblokStory } from '../../../context/StoryblokStoryContext'
import {
  ALL_FROM_CATEGORY_SUFFIX,
  storyAccessorIsAllFromCategoryPath,
} from '../../../model/storyAccessors/storyAccessorIsAllFromCategoryPath'
import { walkStoryblokBlocks } from '../../../walkStoryblokBlocks'

const useShowAllFromCategory = (): boolean => {
  const { story } = useStoryblokStory()

  const pathname = useNextPathname()

  if (storyAccessorIsAllFromCategoryPath(pathname)) {
    return false
  }

  let hasSearch = false

  walkStoryblokBlocks(story.content, ({ component }) => {
    if (component === 'ProductSearch') {
      hasSearch = true
    }
  })

  return !hasSearch
}

/**
 * @returns Ancestors of the current category (excluding the category itself), ordered from the root (farthest) to the immediate parent (closest).
 */
const useAncestors = (): CachedHierarchyEntryResponseDto[] => {
  const { enrichedData } = useStoryblokStory()

  if (!enrichedData?.hierarchy) {
    return []
  }

  return storyblokGetHierarchyCategoryList(enrichedData.hierarchy).slice(0, -1)
}

const LinkSlot = prepareForSlot(NextLink)

type StoryblokCategoryHeaderProps = {
  title?: string
}

export const StoryblokCategoryHeader: FC<StoryblokCategoryHeaderProps> = ({
  title,
}) => {
  const pathname = useNextPathname()

  const showAllFromCategory = useShowAllFromCategory()

  const ancestors = useAncestors()
  const parent = ancestors.at(-1)

  return (
    <section className="container flex flex-col gap-2 desktop:gap-4">
      {ancestors.length > 0 ? (
        <Breadcrumbs>
          {ancestors.map((ancestor) => (
            <Breadcrumb
              className="hidden first:flex desktop:flex"
              key={ancestor.fullSlug}
            >
              <WebUiLink href={cmsStoryblokSlugToPublicUrl(ancestor.fullSlug)}>
                {ancestor.akeneoCategoryCode === 'root_categories'
                  ? 'Home'
                  : ancestor.name}
              </WebUiLink>
            </Breadcrumb>
          ))}
          <Breadcrumb>{title}</Breadcrumb>
        </Breadcrumbs>
      ) : null}
      <div className="flex flex-col items-start gap-2 self-stretch desktop:flex-row desktop:flex-wrap desktop:items-center desktop:gap-4">
        <div className="flex flex-1 items-center">
          {parent ? (
            <Link
              aria-label={parent.name}
              className="shrink-0"
              href={cmsStoryblokSlugToPublicUrl(parent.fullSlug)}
              slots={{
                root: LinkSlot,
              }}
            >
              <IconCaretLeft className="size-11" />
            </Link>
          ) : null}
          {title ? (
            <Heading level={1} size="2xl">
              {title}
            </Heading>
          ) : null}
        </div>
        {showAllFromCategory ? (
          <Button
            endIcon={<IconArrowRight />}
            // use hard link, read more in {@link StoryblokAlgoliaSearchNotFound}
            href={urlNormalizeUri({
              uri: `${pathname}/${ALL_FROM_CATEGORY_SUFFIX}`,
            })}
            size="large"
            variant="text"
          >
            <FormattedMessage id="general.label.ShowAllProducts" />
          </Button>
        ) : null}
      </div>
    </section>
  )
}
